const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const positiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const numericRadius = (value) => {
  if (value === null || value === undefined || value === '' || value === 'all') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const buildSalonSearchRequest = ({
  query = '',
  categoryId = '',
  radiusKm = 'all',
  selectedPlace = null,
  coordinates = null,
  legacyPlaceText = '',
  mode,
  page = 1,
  pageSize = 20,
} = {}) => {
  const trimmedQuery = String(query || '').trim();
  const effectiveMode = mode || (trimmedQuery || selectedPlace ? 'text' : 'nearby');
  const request = {
    mode: effectiveMode,
    page: positiveInteger(page, 1),
    page_size: positiveInteger(pageSize, 20),
  };

  // A selected place already represents the text in the unified field. Sending
  // it again as q would incorrectly constrain the search to salon names.
  if (trimmedQuery && !selectedPlace) request.q = trimmedQuery;
  if (legacyPlaceText.trim()) request.place_text = legacyPlaceText.trim();

  const parsedCategoryId = positiveInteger(categoryId, null);
  if (parsedCategoryId !== null) request.category_id = parsedCategoryId;

  if (selectedPlace) {
    request.place_level = selectedPlace.place_level;
    request.place_value = selectedPlace.place_value;
    if (selectedPlace.place_country_code) {
      request.place_country_code = selectedPlace.place_country_code;
    }
  }

  const radius = numericRadius(radiusKm);
  const coordinateSource = coordinates || selectedPlace;
  const latitude = finiteNumber(coordinateSource?.latitude);
  const longitude = finiteNumber(coordinateSource?.longitude);

  if (effectiveMode === 'nearby') {
    request.radius_km = radius || 25;
    if (latitude !== null) request.latitude = latitude;
    if (longitude !== null) request.longitude = longitude;
  } else if (radius !== null && latitude !== null && longitude !== null) {
    request.radius_km = radius;
    request.latitude = latitude;
    request.longitude = longitude;
  }

  return request;
};

export const buildSearchSalonUrl = ({
  query = '',
  categoryId = '',
  radiusKm = 'all',
  selectedPlace = null,
  coordinates = null,
  legacyPlaceText = '',
  mode = 'text',
  page = 1,
  active = true,
} = {}) => {
  const params = new URLSearchParams();
  const trimmedQuery = String(query || '').trim();
  if (trimmedQuery) params.set('q', trimmedQuery);
  if (legacyPlaceText.trim()) params.set('location', legacyPlaceText.trim());
  if (categoryId) params.set('category', String(categoryId));
  if (radiusKm !== null && radiusKm !== undefined && radiusKm !== 'all') {
    params.set('radius', String(radiusKm));
  }
  if (selectedPlace) {
    params.set('place_level', selectedPlace.place_level || '');
    params.set('place_value', selectedPlace.place_value || '');
    if (selectedPlace.place_country_code) {
      params.set('place_country_code', selectedPlace.place_country_code);
    }
  }
  const coordinateSource = coordinates || selectedPlace;
  if (finiteNumber(coordinateSource?.latitude) !== null) {
    params.set('lat', String(coordinateSource.latitude));
  }
  if (finiteNumber(coordinateSource?.longitude) !== null) {
    params.set('lng', String(coordinateSource.longitude));
  }
  if (mode === 'nearby') params.set('mode', 'nearby');
  if (positiveInteger(page, 1) > 1) params.set('page', String(page));
  if (active && params.toString() === '') params.set('active', '1');
  const queryString = params.toString();
  return queryString ? `/search-salon?${queryString}` : '/search-salon';
};

export const readSalonSearchFromUrl = (search = '') => {
  const params = new URLSearchParams(search);
  const legacyName = params.get('search') || '';
  const legacyPlace = params.get('location') || '';
  const modernQuery = params.get('q') || '';
  const query = modernQuery || legacyName || legacyPlace;
  const legacyPlaceText = legacyPlace && (modernQuery || legacyName)
    ? legacyPlace
    : '';
  const mode = params.get('mode') === 'nearby' ? 'nearby' : 'text';
  const latitude = finiteNumber(params.get('lat'));
  const longitude = finiteNumber(params.get('lng'));
  const placeLevel = params.get('place_level') || '';
  const placeValue = params.get('place_value') || '';
  const selectedPlace = placeLevel && placeValue
    ? {
      kind: 'place',
      label: query || placeValue,
      place_level: placeLevel,
      place_value: placeValue,
      place_country_code: params.get('place_country_code') || '',
      latitude,
      longitude,
    }
    : null;
  const hasActiveSearch = (
    params.get('active') === '1'
    || Boolean(query)
    || Boolean(params.get('category'))
    || Boolean(params.get('categoryId'))
    || Boolean(selectedPlace)
    || mode === 'nearby'
  );

  return {
    hasActiveSearch,
    page: positiveInteger(params.get('page'), 1),
    formValue: {
      query,
      categoryId: params.get('category') || params.get('categoryId') || '',
      radiusKm: params.get('radius') || 'all',
      selectedPlace,
      coordinates: latitude !== null && longitude !== null
        ? { latitude, longitude }
        : null,
      legacyPlaceText,
      mode,
    },
  };
};
